import { Column, Table as TableModel, flexRender } from '@tanstack/react-table';
import React, { ComponentType, useCallback, useMemo, useRef } from 'react';
import { Point } from '../../platform/geom/Point';
import { SimpleDragHandler, useSimpleDrag } from '../../ui/Hooks';
import { CoolTableTheme, Table } from '../../ui/Table';

const sizeProps = (table: TableModel<any>, column: Column<any>) => {
    // eslint-disable-next-line no-underscore-dangle
    const { size: defaultSize } = table._getDefaultColumnDef();
    const colSize = column.columnDef.size;
    if ((!!colSize || colSize === 0) && colSize !== defaultSize) {
        return { width: `${colSize}px` };
    }
    return {};
};

const sortProps = (table: TableModel<any>, column: Column<any>) => {
    if (!column.getCanSort()) return {};
    const current = column.getIsSorted();
    return {
        clickable: true,
        onClick() {
            table.resetSorting();
            if (!current) column.toggleSorting(false);
            if (current === 'asc') column.toggleSorting(true);
        }
    };
};

const findRowAt = (pt: Point) => {
    return (document.elementsFromPoint(pt.x, pt.y) as HTMLElement[])
        .find((x: HTMLElement) => !!x.dataset.row)
        ?.dataset.row;
};

const findNearestTd = (elmt: HTMLElement) => {
    while (elmt && elmt.tagName !== 'TD') {
        elmt = elmt.parentElement;
    }
    return elmt;
};

export type DataTableProps = {
    table: TableModel<any>,
    theme?: ComponentType<{ children: React.ReactNode }>,
    selectFromFirstCellOnly?: boolean,
};

export const DataTable = (({ table, theme, selectFromFirstCellOnly }: DataTableProps) => {

    const Theme = useMemo(() => theme ?? CoolTableTheme, [theme]);

    const elmt = useRef<HTMLTableElement>();
    const shouldSelect = useRef(true);

    const onDrag: SimpleDragHandler = useCallback((dist, start, ticks) => {

        const startRow = table.getRow(findRowAt(start), true);
        const endRow = table.getRow(findRowAt(start.add(dist)), true);
        const { rows: visibleRows } = table.getRowModel();

        if (!startRow || !endRow) return true; // true = keep dragging

        if (ticks === 0) {
            shouldSelect.current = !startRow.getIsSelected();
        }

        const startIndex = visibleRows.findIndex(x => x.id === startRow.id);
        const endIndex = visibleRows.findIndex(x => x.id === endRow.id);
        const [from, to] = [startIndex, endIndex].sort((a, b) => a - b);
        const range = visibleRows.slice(from, to + 1);
        range.forEach(x => {
            x.toggleSelected(shouldSelect.current);
        });

        return true;
    }, [table]);

    const onDragStart = useSimpleDrag(onDrag);

    const onMaybeDragStart = (e: any) => {
        const td = findNearestTd(e.target);
        if (!td) return;
        if (!selectFromFirstCellOnly || td.matches('td:first-child')) {
            onDragStart(e);
        }
    };

    const canSelect = table?.getRowModel()?.rows?.[0]?.getCanSelect();

    return <Theme>
        <Table ref={elmt} disableSelect={canSelect && !selectFromFirstCellOnly}>
            <Table.Head>
                {table.getHeaderGroups().map((headerGroup) => (
                    <Table.Row key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                            return (
                                <Table.HeadCell key={header.id} {...sortProps(table, header.column)} {...sizeProps(table, header.column)}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                    <Table.SortIndicator sort={header.column.getIsSorted()} />
                                </Table.HeadCell>
                            );
                        })}
                    </Table.Row>
                ))}
            </Table.Head>
            <Table.Body>
                {!!table.getRowModel().rows?.length && (
                    table.getRowModel().rows.map((row) => (
                        <Table.Row
                            key={row.id}
                            data-row={row.id}
                            clickable={row.getCanSelect()}
                            selected={row.getIsSelected()}
                            onMouseDown={row.getCanSelect() ? onMaybeDragStart : null}
                        >
                            {row.getVisibleCells().map((cell) => (
                                <Table.Cell key={cell.id} data-row={row.id} data-cell={cell.column.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </Table.Cell>
                            ))}
                        </Table.Row>
                    ))
                )}
            </Table.Body>
        </Table>
    </Theme>;
});
